import { useEffect, useState } from 'react';

import type { PlaybookDefinition, PlaybookType } from '@/api/configuration';
import { listPlaybookDefinitions } from '@/api/configuration';

// --- Types ---

export interface DefinitionSelectorProps {
  /** Currently selected definition ID, or null */
  value: number | null;
  /** Filter definitions by this playbook type */
  playbookType?: PlaybookType;
  /** Called when user selects or clears a definition */
  onChange: (definitionId: number | null, path: string | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

// --- Component ---

export function DefinitionSelector({
  value,
  playbookType,
  onChange,
  disabled = false,
}: DefinitionSelectorProps) {
  const [definitions, setDefinitions] = useState<PlaybookDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDefinitions() {
      setLoading(true);
      setError(null);
      try {
        const data = await listPlaybookDefinitions(playbookType);
        if (!cancelled) {
          setDefinitions(data);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load playbook definitions. You can still enter a path manually.');
          setDefinitions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchDefinitions();

    return () => {
      cancelled = true;
    };
  }, [playbookType]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (selectedValue === '') {
      onChange(null, null);
    } else {
      const id = Number(selectedValue);
      const definition = definitions.find((d) => d.id === id);
      if (definition) {
        onChange(definition.id, definition.path);
      }
    }
  };

  return (
    <div>
      <label
        htmlFor="definition-selector"
        className="block text-xs font-medium text-content-secondary mb-1"
      >
        Playbook Definition
      </label>
      <select
        id="definition-selector"
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled || loading}
        aria-label="Select a playbook definition"
        aria-describedby={error ? 'definition-selector-warning' : undefined}
        className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">
          {loading ? 'Loading definitions...' : '--- Select a definition ---'}
        </option>
        {definitions.map((def) => (
          <option key={def.id} value={def.id}>
            {def.name}
          </option>
        ))}
      </select>
      {error && (
        <p
          id="definition-selector-warning"
          role="alert"
          className="mt-1 text-xs text-amber-700 dark:text-amber-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}
